import styled from 'styled-components';

export const Nav = styled.nav`
  --nav-muted: ${({ theme }) => theme.navMuted};
  height: 88px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: sticky;
  top: 0;
  z-index: 1000;
  padding: 12px 32px 8px;
  background: linear-gradient(180deg, ${({ theme }) => theme.bg}f2, ${({ theme }) => theme.bg}00);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);

  @media (max-width: 1240px) {
    height: 84px;
    padding: 12px 12px 8px;
  }
`;

export const NavbarContainer = styled.div`
  width: min(1180px, 100%);
  min-height: 64px;
  display: grid;
  grid-template-columns: minmax(240px, 1fr) auto auto;
  align-items: center;
  gap: 24px;
  padding: 0 18px 0 22px;
  border-radius: 14px;
  background: ${({ theme }) => theme.navBg};
  border: 1px solid ${({ theme }) => theme.navBorder};
  box-shadow:
    0 6px 20px ${({ theme }) => theme.navShadow},
    inset 0 1px 0 var(--surface);
  backdrop-filter: blur(22px) saturate(1.05);
  -webkit-backdrop-filter: blur(22px) saturate(1.05);

  @media (max-width: 1240px) {
    width: 100%;
    min-height: 70px;
    grid-template-columns: 1fr auto;
    gap: 14px;
    padding: 0 14px 0 16px;
  }

  @media (max-width: 520px) {
    min-height: 66px;
    border-radius: 14px;
    padding: 0 10px 0 12px;
  }
`;

export const NavLogo = styled.a`
  display: flex;
  gap: 16px;
  align-items: center;
  min-width: 0;
  color: ${({ theme }) => theme.text_primary};
  text-decoration: none;

  img {
    width: 42px !important;
    height: 42px !important;
    border-radius: 50% !important;
    object-fit: cover;
    border: 3px solid ${({ theme }) => theme.navControl};
    box-shadow:
      0 10px 26px ${({ theme }) => theme.shadow},
      0 0 0 1px ${({ theme }) => theme.navBorder};
  }

  @media (max-width: 520px) {
    gap: 10px;

    img {
      width: 40px !important;
      height: 40px !important;
    }
  }
`;

export const Span = styled.div`
  padding: 0;
  font-family: 'Instrument Sans', sans-serif;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.025em;
  white-space: nowrap;
  color: ${({ theme }) => theme.navText};

  @media (max-width: 520px) {
    font-size: clamp(14px, 4.4vw, 17px);
  }
`;

export const NavItems = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 22px;
  padding: 0;
  list-style: none;

  @media screen and (max-width: 1240px) {
    display: none;
  }

  @media screen and (max-width: 1180px) {
    gap: 20px;
  }
`;

export const NavLink = styled.a`
  position: relative;
  color: ${({ theme, $active }) => $active ? theme.secondary : theme.text_secondary};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  padding: 18px 0 20px;
  border-radius: 0;
  transition: color 180ms ease, transform 180ms ease;

  &::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: 5px;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${({ theme }) => theme.secondary};
    opacity: ${({ $active }) => $active ? 1 : 0};
    transform: translateX(-50%) scale(${({ $active }) => $active ? 1 : 0.5});
    box-shadow: 0 0 0 4px ${({ theme }) => theme.hover};
    transition: opacity 180ms ease, transform 180ms ease;
  }

  :hover {
    color: ${({ theme }) => theme.secondary};
    transform: translateY(-1px);
  }
`;

export const GitHubButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  color: ${({ theme }) => theme.navText};
  border: 1px solid ${({ theme }) => theme.navBorder};
  background: ${({ theme }) => theme.navControl};
  cursor: pointer;
  text-decoration: none;
  font-size: 16px;
  box-shadow: 0 10px 24px ${({ theme }) => theme.shadow};
  transition: color 160ms ease, background 160ms ease, transform 160ms ease, box-shadow 160ms ease;

  :hover {
    color: ${({ theme }) => theme.secondary};
    background: ${({ theme }) => theme.card_light};
    transform: translateY(-2px);
    box-shadow: 0 16px 32px ${({ theme }) => theme.shadow};
  }
`;

export const ThemeButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  color: ${({ theme }) => theme.navText};
  border: 1px solid ${({ theme }) => theme.navBorder};
  background: ${({ theme }) => theme.navControl};
  cursor: pointer;
  font-size: 18px;
  box-shadow: 0 13px 30px ${({ theme }) => theme.shadow};
  transition: color 160ms ease, background 160ms ease, transform 160ms ease, box-shadow 160ms ease;

  :hover {
    color: ${({ theme }) => theme.secondary};
    background: ${({ theme }) => theme.card_light};
    transform: translateY(-2px) rotate(12deg);
    box-shadow: 0 18px 36px ${({ theme }) => theme.shadow};
  }
`;

export const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;

  @media screen and (max-width: 1240px) {
    display: none;
  }
`;

export const MobileIcon = styled.button`
  display: none;

  @media screen and (max-width: 1240px) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    cursor: pointer;
    color: ${({ theme }) => theme.navText};
    background: ${({ theme }) => theme.navControl};
    border: 1px solid ${({ theme }) => theme.navBorder};
    font-size: 1.25rem;
    box-shadow: none;
  }
`;

export const MobileOverlay = styled.div`
  position:fixed;inset:0;z-index:1100;background:rgba(8,13,22,.45);
`;

export const MobileMenu = styled.div`
  position: fixed;
  inset: 12px;
  left:auto;
  width:min(440px,calc(100% - 24px));
  height: calc(100vh - 24px);
  height: calc(100dvh - 24px);
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 20px;
  overflow-y: auto;
  overscroll-behavior: contain;
  border-radius: 18px;
  background: ${({ theme }) => theme.bgLight};
  border: 1px solid ${({ theme }) => theme.border};
  box-shadow: 0 32px 90px rgba(72, 50, 28, 0.22);
  transform: ${({ isOpen }) => (isOpen ? 'translateY(0)' : 'translateY(-3%)')};
  opacity: ${({ isOpen }) => (isOpen ? '1' : '0')};
  z-index: ${({ isOpen }) => (isOpen ? '1100' : '-1000')};
  >div:first-child{margin-bottom:12px;}
  >div:last-child{margin-top:auto !important;padding-top:16px;}
  button{width:44px;height:44px;border-radius:12px;box-shadow:none;flex-shrink:0;}
  a:focus-visible,button:focus-visible{outline:2px solid ${({ theme }) => theme.secondary};outline-offset:3px;}
`;

export const MobileMenuItems = styled.ul`
  display: flex;
`;

export const MobileMenuLink = styled.a`
  color: ${({ theme }) => theme.text_primary};
  text-decoration: none;
`;

export const MobileMenuButton = styled.a`
  color: ${({ theme }) => theme.text_primary};
`;

export const MobileLink = styled.a`
  color: ${({ theme }) => theme.text_primary};
  font-family: 'Instrument Sans', sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.3;
  letter-spacing: -0.015em;
  cursor: pointer;
  text-decoration: none;
  width: 100%;
  min-height:44px;
  padding:10px 12px;
  border-radius:10px;
  &[aria-current='page']{color:${({ theme }) => theme.secondary};background:${({ theme }) => theme.hover};}
  transition: color 180ms ease, transform 180ms ease;

  :hover {
    color: ${({ theme }) => theme.secondary};
    transform: translateX(8px);
  }
`;

export const MobileNavLogo = styled.a`
  display: flex;
`;
